import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/controls/customControls/dialog";
import { Button } from "../../../components/controls/customControls/button";
import { Input } from "../../../components/controls/customControls/input";
import { Label } from "../../../components/controls/customControls/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/controls/customControls/select";
import { v4 as uuidv4 } from "uuid";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useScheduleData, UseScheduleDataReturn } from "../scheduleData";
import {
  getPatientByPatientId,
  getPatientAppointmentsByPatientId,
  getPatientsListPowerSearch,
  setFilteredPatients,
  addNewPatient,
  addNewPatientMatch,
} from "../../../dispatchers/patientDispatchers";
import { getCompanyVaccineByCompanyId } from "../../../dispatchers/nmsDispatchers";
import useTitanApp from "../../../hooks/useTitanApp";
import {
  RadioGroup,
  RadioGroupItem,
} from "../../../components/controls/customControls//radio-group";
import {
  Alert,
  AlertTitle,
  AlertDescription,
} from "../../../components/controls/customControls/alert";
import {
  AlertCircle,
  ArrowLeft,
  Bell,
  Calendar,
  Search,
  AlertTriangle,
  Syringe,
  Plus,
  CheckSquare,
  Trash2,
} from "lucide-react";
import { trackFirstTimeIntercomEvent } from "@/utils/trackFirstTimeIntercomEvent";
import { getFormattedDate } from "@/utils/FormattedDate";

import { setShowBookingSlots } from "@/dispatchers/scheduleEventsDispatchers";
import { PatientMatchDialog } from "@/pages/Dashboard/ServicesTab/ServicesContainer/ServicesKanban/PatientMatchDialog";
import { useNHS111Data } from "@/lib/nhs111/nhs111Data";

import {
  setConsultationLoadError,
  setShowPermissionModal,
  setShowPermissionMessage,
} from "@/dispatchers/appDispatchers";
import { checkUserPermissions } from "@/utils/checkUserPermissions";
import { accessTitanverseConsultationUpsert } from "@/data/constantStrings";
import { VaccineCombobox } from "../schedule/VaccineComboBox";
import {
  AccordionItem,
  AccordionTrigger,
  Accordion,
  AccordionContent,
} from "@/components/ui/accordion";
import {
  clearDefaultCalenderAppointmentPharmacyServices,
  clearServiceUsersByCompany,
  getDefaultServicesByCompanyIdAndPharmacyId,
} from "@/dispatchers/pharmacyServiceDispatchers";


/**
 * Calculate search relevance score for a patient
 * Higher score = better match
 */
const calculateRelevanceScore = (patient: any, searchQuery: string): number => {
  const query = searchQuery.toLowerCase().trim();
  let score = 0;

  // Extract searchable fields
  const name = (patient.patientName || "").toLowerCase();
  const nhs = (patient.nhsNumber || "").toLowerCase();
  const address = (patient.address || "").toLowerCase();

  // 1. Exact match (highest priority) - 1000 points
  if (name === query) score += 1000;
  if (nhs === query) score += 1000;

  // 2. Starts with query - 500 points
  if (name.startsWith(query)) score += 500;
  if (nhs.startsWith(query)) score += 500;
  if (address.startsWith(query)) score += 300;

  // 3. Word boundary match (word starts with query) - 400 points
  const nameWords = name.split(/\s+/);
  const addressWords = address.split(/\s+/);

  nameWords.forEach((word) => {
    if (word.startsWith(query)) score += 400;
  });

  addressWords.forEach((word) => {
    if (word.startsWith(query)) score += 200;
  });

  // 4. Contains query - 200 points
  if (name.includes(query)) score += 200;
  if (nhs.includes(query)) score += 200;
  if (address.includes(query)) score += 100;

  // 5. Position bonus (earlier in string = higher score)
  const nameIndex = name.indexOf(query);
  if (nameIndex !== -1) {
    score += Math.max(0, 100 - nameIndex * 2); // Earlier positions get higher bonus
  }

  const nhsIndex = nhs.indexOf(query);
  if (nhsIndex !== -1) {
    score += Math.max(0, 100 - nhsIndex * 2);
  }

  // 6. Length similarity bonus (closer to query length = better)
  if (name.length > 0) {
    const lengthDiff = Math.abs(name.length - query.length);
    score += Math.max(0, 50 - lengthDiff * 2);
  }

  // 7. Consecutive character match bonus
  for (let i = 0; i < name.length - query.length + 1; i++) {
    if (name.substring(i, i + query.length) === query) {
      score += 300; // Bonus for consecutive match
    }
  }

  return score;
};

/**
 * Sort patients by relevance to search query
 */
const sortPatientsByRelevance = (
  patients: any[],
  searchQuery: string,
): any[] => {
  if (!searchQuery || searchQuery.trim().length === 0) {
    return patients;
  }

  return [...patients].sort((a, b) => {
    const scoreA = calculateRelevanceScore(a, searchQuery);
    const scoreB = calculateRelevanceScore(b, searchQuery);

    // Higher score first
    return scoreB - scoreA;
  });
};

type BookingForm = {
  patientName: string;
  patientId?: string;
  pharmacist: string;
  serviceType: string;
  date: string;
  time: string;
  duration: string;
  room?: string;
  notes?: string;
  email?: string;
  mobile?: string;
  confirmationMethod?: "email" | "mobile" | undefined;
  // these fields are not part of the react-hook-form inputs; the modal
  // adds them from local state before submitting so the parent can persist
  tasksToComplete?: AppointmentAction[];
  vaccinesAdminister?: AppointmentVaccine[];
};

export interface AppointmentVaccine {
  id: string;
  name: string;
  vaccineName?: string;
  dose?: string;
  notes?: string;
  completed?: boolean;
  prescriptionItemId?: string;
  manufacturer?: string;
}
interface Booking {
  id: string;
  patientName: string;
  pharmacist: string;
  serviceType: string;
  date: string;
  time: string;
  duration: number;
  room?: string; // display name
  roomId?: string; // id for prefilling
  notes?: string;
  patientId?: string;
  email?: string;
  contactNumber?: string;
  confirmationMethod?: "email" | "mobile";
  serviceId?: string;
}

interface BookingModalProps {
  trigger?: React.ReactNode;
  mode?: "create" | "edit";
  booking?: Booking;
  onSave: (
    booking: BookingForm & {
      tasksToComplete?: AppointmentAction[];
      vaccinesAdminister?: AppointmentVaccine[];
    },
  ) => void;
  onCancel?: () => void;
  editMode?: Booking; // Keep for backward compatibility
  pharmacists: string[];
  serviceTypes?: string[];
  rooms?: string[];
  initialDate?: string;
  initialTime?: string;
  initialServiceTypeId?: string;
  initialPharmacistName?: string;
  initialPatientName?: string;
  initialPatientId?: string;
  initialPatientEmail?: string;
  initialPatientPhone?: string;
  isDarkMode?: boolean;
  isPartiallyMatched?: boolean; // Made optional
  onStartConsultation?: (booking: Booking) => void;
  onDelete?: (bookingId: string) => void;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  onPatientMatchClick?: (matchingPatients: any[]) => void; // Made optional
  isReadOnly: boolean;
  tasksToComplete?: AppointmentAction[];
  vaccinesAdminister?: AppointmentVaccine[];
  newTaskLabel?: string;
  newVaccineId?: string;
  setNewTaskLabel?: (label: string) => void;
  setNewVaccineId?: (id: string) => void;
  onTaskAdd?: (label: string) => void;
  onTaskRemove?: (id: string) => void;
  onVaccineAdd?: (vaccine: AppointmentVaccine | string) => void; // string kept for backward compat
  onVaccineRemove?: (id: string) => void;
}

interface AppointmentAction {
  id: string;
  label: string;
  completed?: boolean;
}

export function BookingModal({
  trigger,
  mode,
  booking,
  onSave,
  onCancel,
  editMode,
  rooms = [],
  initialDate,
  initialTime,
  initialServiceTypeId,
  initialPharmacistName,
  initialPatientName,
  initialPatientId,
  initialPatientEmail,
  initialPatientPhone,
  isDarkMode = false,
  onStartConsultation,
  onDelete,
  isOpen: externalIsOpen,
  onOpenChange,
  onPatientMatchClick,
  isPartiallyMatched = false, // Default value
  isReadOnly = false,
  tasksToComplete,
  vaccinesAdminister,
  newTaskLabel = "",
  newVaccineId = "",
  setNewTaskLabel = () => {},
  setNewVaccineId = () => {},
  onTaskAdd = () => {},
  onTaskRemove = () => {},
  onVaccineAdd = () => {},
  onVaccineRemove = () => {},
}: BookingModalProps) {
  // internal state for task/vaccine when parent doesn’t control them
  const [internalTaskLabel, setInternalTaskLabel] =
    React.useState(newTaskLabel);
  const [internalVaccineId, setInternalVaccineId] =
    React.useState(newVaccineId);

  // local arrays so we can add/remove multiple entries
  const [localTasks, setLocalTasks] = React.useState<AppointmentAction[]>(
    tasksToComplete ?? [],
  );
  const [localVaccines, setLocalVaccines] = React.useState<
    AppointmentVaccine[]
  >(vaccinesAdminister ?? []);

  // Define isEditMode early so effects can use it
  const isEditMode = mode === "edit" || !!editMode;

  // Define currentBooking early - needed by effects
  const currentBooking = booking || editMode;

  // Define open early - needed by effects
  const [internalOpen, setInternalOpen] = React.useState(false);
  const [isPharmacistLoading, setIsPharmacistLoading] = React.useState(false);
  const [BookingConfirmationLoading, setBookingConfirmationLoading] =
    React.useState(false);
  const [isRoomsLoading, setIsRoomsLoading] = React.useState(false);
  const open =
    externalIsOpen !== undefined ? externalIsOpen : mode ? true : internalOpen;
  const setOpen = React.useCallback(
    (value: boolean) => {
      if (onOpenChange) {
        onOpenChange(value);
      } else if (onCancel && !value) {
        onCancel();
      } else {
        setInternalOpen(value);
      }
    },
    [onOpenChange, onCancel],
  );

  React.useEffect(() => {
    setInternalTaskLabel(newTaskLabel);
  }, [newTaskLabel]);
  React.useEffect(() => {
    setInternalVaccineId(newVaccineId);
  }, [newVaccineId]);

  // synchronize with incoming props when the modal is opened (e.g. editing an
  // existing booking). ONLY in edit mode - never for new appointments
  React.useEffect(() => {
    if (open && isEditMode && currentBooking) {
      const rawTasks =
        (currentBooking as any)?.tasksToComplete ||
        (currentBooking as any)?.TasksToComplete ||
        tasksToComplete ||
        [];

      const parsedTasks: AppointmentAction[] = Array.isArray(rawTasks)
        ? rawTasks.map((t: any, idx: number) => {
            if (typeof t === "string") {
              return { id: String(idx + 1), label: t, text: t, completed: false };
            }
            return {
              id: String(t.id || t.Id || t._id || idx + 1),
              label: t.label || t.text || t.name || t.Name || String(t),
              text: t.text || t.label || t.name || t.Name || String(t),
              completed: Boolean(t.completed),
            };
          })
        : [];

      const rawVaccines =
        (currentBooking as any)?.vaccinesAdminister ||
        (currentBooking as any)?.vaccinesToAdminister ||
        (currentBooking as any)?.VaccinesAdminister ||
        (currentBooking as any)?.VaccinesToAdminister ||
        vaccinesAdminister ||
        [];

      const parsedVaccines: AppointmentVaccine[] = Array.isArray(rawVaccines)
        ? rawVaccines.map((v: any, idx: number) => {
            if (typeof v === "string") {
              return { id: String(idx + 1), name: v, vaccineName: v, dose: "" };
            }
            const vName =
              v.VaccineName || v.vaccineName || v.Name || v.name || "Vaccine";
            return {
              id: String(v.Id || v.id || v._id || idx + 1),
              name: vName,
              vaccineName: vName,
              dose: v.Manufacturer || v.dose || "",
              manufacturer: v.Manufacturer || "",
              prescriptionItemId: v.PrescriptionItemId || "",
            };
          })
        : [];

      setLocalTasks(parsedTasks);
      setLocalVaccines(parsedVaccines);
    }
  }, [tasksToComplete, vaccinesAdminister, isEditMode, open, currentBooking]);

  // For new appointments, clear state when modal opens and keep cleared
  React.useEffect(() => {
    if (open && !isEditMode && !currentBooking) {
      setLocalTasks([]);
      setLocalVaccines([]);
      setInternalTaskLabel("");
      setInternalVaccineId("");
    }
  }, [open, isEditMode, currentBooking]);

  const taskLabel = internalTaskLabel;
  const vaccineIdState = internalVaccineId;

  const handleTaskLabelChange = (val: string) => {
    setInternalTaskLabel(val);
    setNewTaskLabel(val);
  };

  const validateTemplateAvailability = (
    data: BookingForm,
    confirmCancelTemplates: any,
    setValue: any,
    setError: any,
    operationType: string,
  ): boolean => {
    if (data.confirmationMethod) {
      if (data.confirmationMethod === "email") {
        if (operationType === "insert") {
          if (
            !confirmCancelTemplates?.confirmationCancellation?.emailConfirmation?.trim()
          ) {
            setValue("confirmationMethod", "" as any);
            setError("confirmationMethod", {
              type: "manual",
              message:
                "No Email confirmation template is configured for this service.",
            });
            return false;
          }
        } else if (operationType === "delete") {
          if (
            !confirmCancelTemplates?.confirmationCancellation?.emailCancellation?.trim()
          ) {
            setValue("confirmationMethod", "" as any);
            setError("confirmationMethod", {
              type: "manual",
              message:
                "No Email cancellation template is configured for this service.",
            });
            return false;
          }
        } else {
          if (!confirmCancelTemplates?.reschedule?.emailReschedule?.trim()) {
            setValue("confirmationMethod", "" as any);
            setError("confirmationMethod", {
              type: "manual",
              message:
                "No Email reschedule template is configured for this service.",
            });
            return false;
          }
        }
      } else if (data.confirmationMethod === "mobile") {
        if (operationType === "insert") {
          if (
            !confirmCancelTemplates?.confirmationCancellation?.smsConfirmation?.trim()
          ) {
            setValue("confirmationMethod", "" as any);
            setError("confirmationMethod", {
              type: "manual",
              message:
                "No SMS confirmation template is configured for this service.",
            });
            return false;
          }
        } else if (operationType === "delete") {
          if (
            !confirmCancelTemplates?.confirmationCancellation?.smsCancellation?.trim()
          ) {
            setValue("confirmationMethod", "" as any);
            setError("confirmationMethod", {
              type: "manual",
              message:
                "No SMS cancellation template is configured for this service.",
            });
            return false;
          }
        } else {
          if (!confirmCancelTemplates?.reschedule?.smsReschedule?.trim()) {
            setValue("confirmationMethod", "" as any);
            setError("confirmationMethod", {
              type: "manual",
              message:
                "No SMS reschedule template is configured for this service.",
            });
            return false;
          }
        }
      }
    }
    return true;
  };

  const handleDelete = () => {
    const formData = getValues();
    if (
      !validateTemplateAvailability(
        formData,
        confirmCancelTemplates,
        setValue,
        setError,
        "delete",
      )
    ) {
      return;
    }
    setShowDeleteConfirm(true);
  };

  const handleVaccineIdChange = (val: string) => {
    setInternalVaccineId(val);
    setNewVaccineId(val);
  };

  const handleAddTask = (label: string) => {
    if (!label.trim()) {
      console.warn("attempted to add empty task");
      return;
    }
    const newTask: AppointmentAction = { id: uuidv4(), label };
    setLocalTasks((prev) => {
      const updated = [...prev, newTask];
      return updated;
    });
    onTaskAdd(label);
    handleTaskLabelChange("");
  };

  const handleRemoveTask = (id: string) => {
    setLocalTasks((prev) => {
      const updated = prev.filter((t) => t.id !== id);
      return updated;
    });
    onTaskRemove(id);
  };
  const handleRemoveVaccine = (id: string) => {
    setLocalVaccines((prev) => prev.filter((v) => v.id !== id));
    onVaccineRemove && onVaccineRemove(id);
  };
  const handleAddVaccine = (id: string) => {
    if (!id.trim()) {
      console.warn("attempted to add vaccine with empty id");
      return;
    }
    const selected = flattenedProducts.find((v: any) => v.uniqueId === id);
    const name = selected ? selected.vaccineName : id;

    const prescriptionItemId =
      selected?.selectedProduct?.prescribedItemId || "";
    const manufacturer =
      selected?.selectedProduct?.manufacturerName ||
      selected?.manufacturer ||
      "";

    const newV: AppointmentVaccine = {
      id: uuidv4(),
      name,
      vaccineName: name,
      prescriptionItemId,
      manufacturer,
    };
    setLocalVaccines((prev) => {
      const updated = [...prev, newV];
      return updated;
    });
    onVaccineAdd && onVaccineAdd(newV);
    handleVaccineIdChange("");
  };

  // Use mode/booking if provided, otherwise fall back to editMode for backward compatibility
  const s = useScheduleData();
  const { appState, patientState, scheduleEventState, pharmacyServiceState } =
    useTitanApp();
  const { defaultCalenderAppointmentPharmacyServices, ClinicalServiceBuilder } =
    pharmacyServiceState || {};
  const { currentPharmacyId, companyId } = appState || {};
  const { filteredPatients, selectedPatientId } = patientState || {};
  const { showBookingSlots, bookingSlotsMode, showBacktoSlotsButton } =
    scheduleEventState || {};

  const { nmsServiceState } = useTitanApp();
  const { companyVaccines } = nmsServiceState || {};

  const confirmCancelTemplates = ClinicalServiceBuilder[0]?.product;

  const flattenedProducts = React.useMemo(() => {
    const results = [];
    const seenIds = new Set();
    (companyVaccines || []).forEach((item: any) => {
      const vaccine =
        item?.value && typeof item.value === "object" ? item.value : item;
      if (vaccine && vaccine.vaccineName) {
        (vaccine.products || []).forEach((product: any) => {
          const uniqueId = `${vaccine._id}-${product.productId}`;
          if (!seenIds.has(uniqueId)) {
            results.push({
              ...vaccine,
              selectedProduct: product,
              uniqueId,
            });
            seenIds.add(uniqueId);
          }
        });
      }
    });
    return results;
  }, [companyVaccines]);
  const [showMatchDialog, setShowMatchDialog] = React.useState(false);

  // Local state for partial matches
  const [localPartialMatch, setLocalPartialMatch] = React.useState(false);
  const [localMatchingPatients, setLocalMatchingPatients] = React.useState<
    any[]
  >([]);

  const {
    servicePharmacists = [],
    serviceTypesInfo = [],
    isEventCalendarLoading = false,
    isPatientDetailsLoading,
    onServiceTypeSelected,
    onShowEditBooking,
    onPharmacistSelected,
    onAnyPharmacistSelected,
    serviceRooms = [],
    pharmacyRooms = [],
    serviceDurations = [],
    availableDates = [],
    getAvailableTimesForDate,
    getAggregatedTimesForDate,
    formatDateFromDate,
    validatePharmacySlots,
    validateBookingConflicts,
    handleGetAppointmentRooms,
    currentUser,
    selectedPatientConsultation,
  } = (s || {}) as Partial<UseScheduleDataReturn>;
  const [selectedPatient, setSelectedPatient] = React.useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);

  const handlePatientMatch = (localPatientId: string) => {
    // Find the matched patient from localMatchingPatients
    const matchedPatient = [
      ...(localMatchingPatients.length > 0
        ? localMatchingPatients
        : filteredPatients || []),
    ].find((p) => (p.patientID || p._id) === localPatientId);

    if (matchedPatient) {
      // Create the patient name for display
      const patientDisplayName =
        matchedPatient.patientName ||
        `${matchedPatient.foreName || ""} ${matchedPatient.surName || ""}`.trim();

      setPatientSearchQuery(patientDisplayName);

      setValue("patientName", patientDisplayName, {
        shouldValidate: true,
        shouldDirty: true,
      });

      setValue("patientId", localPatientId, {
        shouldValidate: true,
        shouldDirty: true,
      });

      // Set email if available
      if (matchedPatient.email) {
        setValue("email", matchedPatient.email, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }

      // Set mobile if available
      if (matchedPatient.contacts?.length > 0) {
        const mobileContact = matchedPatient.contacts.find(
          (c) => c.contactType === 4,
        );
        if (mobileContact?.value) {
          setValue("mobile", mobileContact.value, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }
      }

      setShowPatientResults(false);
    }

    // Close the dialog and clear partial match state
    setShowMatchDialog(false);
    setLocalPartialMatch(false);
    setLocalMatchingPatients([]);
  };
  const initialValues = {
    PersonalDetails: {
      ForeName: "",
      MiddleName: "",
      SurName: "",
      DOB: null,
    },
    ContactInformation: {
      Telephone: "",
      Email: "",
    },
  };
  const {
    NHSEmergencyServiceData,
    NHSEmergencyPatientData,
    formatDate,
    matchDialogOpen,
    setMatchDialogModal,
    handleCardClick,
    matchPatient,
    isNHSEmergencyPatientLoading,
    handleAddNewPatient,
  } = useNHS111Data();
  // Patient search state
  const [patientSearchQuery, setPatientSearchQuery] = React.useState("");
  const [showQuickAddPatient, setShowQuickAddPatient] = React.useState(false);
  const [showPatientResults, setShowPatientResults] = React.useState(false);
  const [patientSearchLoading, setPatientSearchLoading] = React.useState(false);
  const [quickAddDefaults, setQuickAddDefaults] = React.useState({
    forename: "",
    surname: "",
    dateOfBirth: "",
    email: "",
    telephone: "",
  });

  type QuickAddForm = {
    forename: string;
    surname: string;
    dateOfBirth: string;
    email?: string;
    telephone?: string;
  };

  function InlineQuickAddPatient({
    isDarkMode,
    defaultValues,
    onCancel,
    onSave,
  }: {
    isDarkMode: boolean;
    defaultValues: QuickAddForm;
    onCancel: () => void;
    onSave: (patient: QuickAddForm) => void; // Change to pass the form data
  }) {
    const [form, setForm] = React.useState<QuickAddForm>(defaultValues);
    const [isSaving, setIsSaving] = React.useState(false);
    const [fieldErrors, setFieldErrors] = React.useState<
      Partial<Record<keyof QuickAddForm, string>>
    >({});
    const handleChange = (field: keyof QuickAddForm, value: string) => {
      setForm((prev) => ({ ...prev, [field]: value }));
      if (fieldErrors[field]) {
        setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    };

    const handleSubmit = async () => {
      const errors: Partial<Record<keyof QuickAddForm, string>> = {};
      if (!form.forename.trim()) errors.forename = "Forename is required";
      if (!form.surname.trim()) errors.surname = "Surname is required";
      if (!form.dateOfBirth) errors.dateOfBirth = "Date of birth is required";

      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        return;
      }
      setIsSaving(true);

      try {
        // Pass the form data to the parent
        await onSave(form);
      } catch (err) {
        console.error("Error saving patient:", err);
        setIsSaving(false);
      }
    };

    return (
      <div
        className={`rounded-lg border p-4 space-y-3 ${
          isDarkMode
            ? "bg-gray-700/50 border-gray-600"
            : "bg-blue-50/50 border-blue-200"
        }`}
      >
        {/* Rest of your component remains the same */}
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold">Quick Add Patient</span>
          <button
            type="button"
            onClick={onCancel}
            className="text-xs text-blue-600"
          >
            Back to search
          </button>
        </div>

        {/* <div className="grid grid-cols-2 gap-3">
          <input
            placeholder="Forename *"
            type="text"
            value={form.forename}
            onChange={(e) => handleChange("forename", e.target.value)}
            className="border rounded px-2 py-1 text-sm"
          />
          <input
            placeholder="Surname *"
            value={form.surname}
            onChange={(e) => handleChange("surname", e.target.value)}
            className="border rounded px-2 py-1 text-sm"
          />
        </div> */}

        {/* <input
          type="date"
          value={form.dateOfBirth}
          onChange={(e) => handleChange("dateOfBirth", e.target.value)}
          className="w-full border rounded px-2 py-1 text-sm"
        /> */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <input
              placeholder="Forename *"
              type="text"
              value={form.forename}
              onChange={(e) => handleChange("forename", e.target.value)}
              className={`w-full border rounded px-2 py-1 text-sm ${
                fieldErrors.forename ? "border-red-500" : ""
              }`}
            />
            {fieldErrors.forename && (
              <p className="text-xs text-red-500">{fieldErrors.forename}</p>
            )}
          </div>
          <div className="space-y-1">
            <input
              placeholder="Surname *"
              value={form.surname}
              onChange={(e) => handleChange("surname", e.target.value)}
              className={`w-full border rounded px-2 py-1 text-sm ${
                fieldErrors.surname ? "border-red-500" : ""
              }`}
            />
            {fieldErrors.surname && (
              <p className="text-xs text-red-500">{fieldErrors.surname}</p>
            )}
          </div>
        </div>
        <div className="space-y-1">
          <input
            type="date"
            value={form.dateOfBirth}
            onChange={(e) => handleChange("dateOfBirth", e.target.value)}
            className={`w-full border rounded px-2 py-1 text-sm ${
              fieldErrors.dateOfBirth ? "border-red-500" : ""
            }`}
          />
          {fieldErrors.dateOfBirth && (
            <p className="text-xs text-red-500">{fieldErrors.dateOfBirth}</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <input
            placeholder="Email"
            value={form.email}
            onChange={(e) => handleChange("email", e.target.value)}
            className="border rounded px-2 py-1 text-sm"
          />
          <input
            placeholder="Telephone"
            value={form.telephone}
            onChange={(e) => handleChange("telephone", e.target.value)}
            className="border rounded px-2 py-1 text-sm"
          />
        </div>

        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 border rounded py-1 text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={isSaving}
            onClick={handleSubmit}
            className="flex-1 bg-blue-600 text-white rounded py-1 text-sm"
          >
            {isSaving ? "Saving..." : "Add Patient"}
          </button>
        </div>
      </div>
    );
  }
  const handleAddPatient = (query?: string) => {
    if (query) {
      const parts = query.trim().split(" ");
      const first = parts[0] || "";
      const last = parts.slice(1).join(" ") || "";

      setQuickAddDefaults({
        forename: first,
        surname: last,
        dateOfBirth: "",
        email: "",
        telephone: "",
      });
    }

    setShowQuickAddPatient(true);
  };
  // Add these helper functions inside your BookingModal component
  const calculateMatchScore = (
    localPatient: any,
    sourcePatient: any,
  ): number => {
    if (!sourcePatient) return 0;

    let score = 0;
    const weights = {
      name: 35,
      dob: 30,
      email: 15,
      phone: 10,
      postcode: 10,
    };

    // Name match (compare forename and surname separately for better accuracy)
    const localForename = (localPatient.foreName || "").toLowerCase().trim();
    const localSurname = (localPatient.surName || "").toLowerCase().trim();
    const sourceForename = (
      sourcePatient.foreName ||
      sourcePatient.patientName?.split(" ")[0] ||
      ""
    )
      .toLowerCase()
      .trim();
    const sourceSurname = (
      sourcePatient.surName ||
      sourcePatient.patientName?.split(" ").slice(1).join(" ") ||
      ""
    )
      .toLowerCase()
      .trim();

    if (localForename === sourceForename && localSurname === sourceSurname) {
      score += weights.name; // Exact full name match
    } else if (localForename === sourceForename) {
      score += weights.name * 0.6; // First name match only
    } else if (localSurname === sourceSurname) {
      score += weights.name * 0.4; // Last name match only
    } else {
      // Partial name matching
      const localFullName = `${localForename} ${localSurname}`;
      const sourceFullName = `${sourceForename} ${sourceSurname}`;
      if (
        localFullName.includes(sourceForename) ||
        sourceFullName.includes(localForename)
      ) {
        score += weights.name * 0.3;
      }
    }

    // DOB match
    if (localPatient.dob && sourcePatient.dob) {
      const localDob = new Date(localPatient.dob).toDateString();
      const sourceDob = new Date(sourcePatient.dob).toDateString();
      if (localDob === sourceDob) {
        score += weights.dob;
      } else {
        // Check if year and month match (partial DOB match)
        const localDate = new Date(localPatient.dob);
        const sourceDate = new Date(sourcePatient.dob);
        if (
          localDate.getFullYear() === sourceDate.getFullYear() &&
          localDate.getMonth() === sourceDate.getMonth()
        ) {
          score += weights.dob * 0.5;
        }
      }
    }

    // Email match
    if (localPatient.email && sourcePatient.email) {
      if (
        localPatient.email.toLowerCase() === sourcePatient.email.toLowerCase()
      ) {
        score += weights.email;
      }
    }

    // Phone match
    if (
      localPatient.contacts?.length > 0 &&
      sourcePatient.contacts?.length > 0
    ) {
      const localPhone = localPatient.contacts
        .find((c: any) => c.contactType === 4)
        ?.value?.replace(/\D/g, "");
      const sourcePhone = sourcePatient.contacts
        .find((c: any) => c.contactType === 4)
        ?.value?.replace(/\D/g, "");

      if (localPhone && sourcePhone && localPhone === sourcePhone) {
        score += weights.phone;
      }
    }

    // Postcode match
    const extractPostcode = (address: string) => {
      const postcodeMatch = address?.match(
        /[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}/i,
      );
      return postcodeMatch
        ? postcodeMatch[0].replace(/\s/g, "").toUpperCase()
        : "";
    };

    const localPostcode = extractPostcode(localPatient.address || "");
    const sourcePostcode = extractPostcode(sourcePatient.address || "");

    if (localPostcode && sourcePostcode && localPostcode === sourcePostcode) {
      score += weights.postcode;
    } else if (
      localPostcode &&
      sourcePostcode &&
      localPostcode.substring(0, 3) === sourcePostcode.substring(0, 3)
    ) {
      score += weights.postcode * 0.5; // Partial postcode match (first part)
    }

    return Math.min(100, Math.round(score));
  };

  const generateMatchReasons = (
    localPatient: any,
    sourcePatient: any,
  ): string[] => {
    const reasons: string[] = [];

    if (!sourcePatient) return reasons;

    const localForename = (localPatient.foreName || "").toLowerCase().trim();
    const localSurname = (localPatient.surName || "").toLowerCase().trim();
    const sourceForename = (
      sourcePatient.foreName ||
      sourcePatient.patientName?.split(" ")[0] ||
      ""
    )
      .toLowerCase()
      .trim();
    const sourceSurname = (
      sourcePatient.surName ||
      sourcePatient.patientName?.split(" ").slice(1).join(" ") ||
      ""
    )
      .toLowerCase()
      .trim();

    if (localForename === sourceForename && localSurname === sourceSurname) {
      reasons.push("Exact name match");
    } else if (localForename === sourceForename) {
      reasons.push("First name matches");
    } else if (localSurname === sourceSurname) {
      reasons.push("Last name matches");
    }

    if (localPatient.dob && sourcePatient.dob) {
      const localDob = new Date(localPatient.dob).toDateString();
      const sourceDob = new Date(sourcePatient.dob).toDateString();
      if (localDob === sourceDob) {
        reasons.push("Date of birth matches");
      }
    }

    if (localPatient.email && sourcePatient.email) {
      if (
        localPatient.email.toLowerCase() === sourcePatient.email.toLowerCase()
      ) {
        reasons.push("Email matches");
      }
    }

    if (
      localPatient.contacts?.length > 0 &&
      sourcePatient.contacts?.length > 0
    ) {
      const localPhone = localPatient.contacts.find(
        (c: any) => c.contactType === 4,
      )?.value;
      const sourcePhone = sourcePatient.contacts.find(
        (c: any) => c.contactType === 4,
      )?.value;

      if (localPhone && sourcePhone && localPhone === sourcePhone) {
        reasons.push("Phone number matches");
      }
    }

    const extractPostcode = (address: string) => {
      const postcodeMatch = address?.match(
        /[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}/i,
      );
      return postcodeMatch ? postcodeMatch[0] : "";
    };

    const localPostcode = extractPostcode(localPatient.address || "");
    const sourcePostcode = extractPostcode(sourcePatient.address || "");

    if (localPostcode && sourcePostcode && localPostcode === sourcePostcode) {
      reasons.push("Postcode matches");
    }

    return reasons;
  };
  const handleSavePatient = async (values) => {
    const finalPayload = {
      patientID: uuidv4(),
      foreName: values.forename || "",
      surName: values.surname || "",
      dob: values.dateOfBirth ? new Date(values.dateOfBirth).toISOString() : "",
      contacts: values.telephone
        ? [
            {
              patientContactId: uuidv4(),
              contactType: 4,
              value: values.telephone,
            },
          ]
        : [],
      email: values.email || "",
      companyID: companyId,
      pharmacyID: currentPharmacyId,
      createdOn: new Date(),
    };

    try {
      const result = await addNewPatientMatch(finalPayload);

      const message = result?.message;
      const patientDetailsList = result?.patientDetailsModel || [];
      const addedPatient = result?.addedPatient;

      if (message !== "Patient added successfully.") {
        setShowQuickAddPatient(true);
        return;
      }

      // Calculate if there are partial matches
      const hasPartialMatches = patientDetailsList.length > 1;

      if (addedPatient) {
        // Store ALL matching patients
        setLocalMatchingPatients(patientDetailsList);
        setLocalPartialMatch(hasPartialMatches);

        const newPatient = {
          patientID: addedPatient.patientID || finalPayload.patientID,
          patientName:
            `${addedPatient.foreName || values.forename} ${addedPatient.surName || values.surname}`.trim(),
          email: addedPatient.email || values.email || "",
          contacts:
            addedPatient.contacts?.length > 0
              ? addedPatient.contacts
              : values.telephone
                ? [{ contactType: 4, value: values.telephone }]
                : [],
        };

        // Set form values
        setValue("patientName", newPatient.patientName, {
          shouldValidate: true,
          shouldDirty: true,
        });

        setValue("patientId", String(newPatient.patientID), {
          shouldValidate: true,
          shouldDirty: true,
        });

        if (newPatient.email) {
          setValue("email", newPatient.email, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }

        if (newPatient.contacts?.length > 0) {
          const mobileContact = newPatient.contacts.find(
            (c) => c.contactType === 4,
          );
          if (mobileContact?.value) {
            setValue("mobile", mobileContact.value, {
              shouldValidate: true,
              shouldDirty: true,
            });
          }
        }

        // Set the search query to the new patient name
        setPatientSearchQuery(newPatient.patientName);

        // Close the quick add form
        setShowQuickAddPatient(false);

        // Add to cached patients
        setCachedPatients((prev) => {
          const exists = prev.some((p) => p.patientID === newPatient.patientID);
          if (!exists) {
            return [...prev, newPatient];
          }
          return prev;
        });
      }
    } catch (error) {
      console.error("Error while adding patient:", error);
    }
  };

  const [cachedPatients, setCachedPatients] = React.useState<any[]>([]);
  const [lastApiKeyword, setLastApiKeyword] = React.useState("");

  const isConsultationCompleted =
    selectedPatientConsultation?.consultationStatus === 1;
  const isCancelledAppointment = (currentBooking as any)?.eventStatus === 4;

  // Sort patients by relevance
  const sortedPatients = React.useMemo(() => {
    return sortPatientsByRelevance(filteredPatients || [], patientSearchQuery);
  }, [filteredPatients, patientSearchQuery]);
  const hasConsultationPermission = checkUserPermissions(
    accessTitanverseConsultationUpsert,
  );

  // load company vaccine catalog when modal opens
  React.useEffect(() => {
    if (open && companyId) {
      clearDefaultCalenderAppointmentPharmacyServices();
      clearServiceUsersByCompany();
      getCompanyVaccineByCompanyId(companyId).catch((err) => {
        console.error("Error fetching company vaccines", err);
      });

      if (currentPharmacyId) {
        getDefaultServicesByCompanyIdAndPharmacyId({
          companyId,
          pharmacyId: currentPharmacyId,
          appointmentDate:
            initialDate ?? new Date().toISOString().split("T")[0],
        });
      }
    }
  }, [open, companyId, currentPharmacyId]);

  // Map the API response to the shape used in the dropdown
  const mappedServices = React.useMemo(() => {
    const services = defaultCalenderAppointmentPharmacyServices;

    const mapped = services
      .filter(
        (s: any) => s.isActive && s.serviceType !== 1 && s.serviceType !== 3,
      )
      .map((s: any) => ({
        _id: s._id,
        name: s.serviceName,
        serviceType: s.serviceType,
        serviceDuration: s.serviceDuration,
        isRoomRequired: s.isRoomRequired,
      }));
    
    return mapped;
  }, [defaultCalenderAppointmentPharmacyServices]);

  // Dynamic schema to require room when the selected service mandates it
  const bookingSchema = React.useMemo(() => {
    return z
      .object({
        patientName: z.string().min(1, "Patient name is required"),
        patientId: z.string().optional(),
        pharmacist: z.string().min(1, "Pharmacist is required"),
        serviceType: z.string().min(1, "Service type is required"),
        date: z.string().min(1, "Date is required"),
        time: z.string().min(1, "Time is required"),
        duration: z.string().min(1, "Duration is required"),
        room: z.string().optional(),
        notes: z.string().optional(),
        email: z
          .string()
          .trim()
          .max(254)
          .email("Invalid email address")
          .optional()
          .or(z.literal("")),
        mobile: z
          .string()
          .trim()
          .max(20)
          .regex(/^[0-9+()\-\s]*$/i, "Invalid phone number")
          .optional()
          .or(z.literal("")),
        confirmationMethod: z
          .union([z.enum(["email", "mobile"]), z.literal(""), z.undefined()])
          .transform((v) => (v === "" ? undefined : v))
          .optional(),
      })
      .superRefine((data, ctx) => {
        const svc = (mappedServices || []).find(
          (s: any) => s?._id === data.serviceType,
        );
        if (svc && (svc as any).isRoomRequired) {
          const v = (data.room || "").trim();
          if (!v) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["room"],
              message: "Room is required for this service",
            });
          }
        }
        if (data.confirmationMethod === "email") {
          const v = (data.email || "").trim();
          if (!v) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["email"],
              message: "Email is required for email confirmation",
            });
          }
        }
        if (data.confirmationMethod === "mobile") {
          const v = (data.mobile || "").trim();
          if (!v) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["mobile"],
              message: "Mobile is required for SMS confirmation",
            });
          }
        }
      });
  }, [mappedServices]);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    getValues,
    setError,
    formState: { errors },
  } = useForm<BookingForm>({
    resolver: zodResolver(bookingSchema),
    defaultValues: {
      duration: "",
      date: initialDate || "",
      time: initialTime || "",
      serviceType: initialServiceTypeId || "",
      email: "",
      mobile: "",
      confirmationMethod: undefined,
    },
  });

  const handlePatientSearch = React.useCallback(
    async (query: string) => {
      // Update state first
      setPatientSearchQuery(query);

      // Clear if empty
      if (query.length === 0) {
        setShowPatientResults(false);
        return;
      }

      const normalizedQuery = query.trim().toLowerCase();

      if (
        isEditMode ||
        !normalizedQuery ||
        normalizedQuery.length < 2 ||
        !currentPharmacyId
      ) {
        return;
      }

      // Check if we need to hit the API
      const shouldHitApi =
        !lastApiKeyword || !normalizedQuery.startsWith(lastApiKeyword);

      if (shouldHitApi) {
        setPatientSearchLoading(true);
        try {
          const payload = {
            keyword: normalizedQuery,
            practitioners: "",
            treatmentPlan: "",
            isTitanSearch: false,
            consultationStatus: 0,
            pharmacyID: currentPharmacyId,
            companyId: companyId,
          };

          await getPatientsListPowerSearch(payload);
          setLastApiKeyword(normalizedQuery);
          setShowPatientResults(true);
        } catch (error) {
          console.error("Failed to fetch patients:", error);
        } finally {
          setPatientSearchLoading(false);
        }
        return;
      }

      // Local filtering for substrings
      const locallyFiltered = cachedPatients.filter((p) =>
        [p.patientName, p.nhsNumber, p.address]
          .filter(Boolean)
          .some((field) => field.toLowerCase().includes(normalizedQuery)),
      );

      setFilteredPatients(locallyFiltered);
      setShowPatientResults(true);
    },
    [currentPharmacyId, isEditMode, lastApiKeyword, cachedPatients],
  );

  const handlePatientSelect = React.useCallback(
    (patient: any) => {
      if (isEditMode || !patient) return;

      if (patient && patient.patientName) {
        setValue("patientName", patient.patientName, {
          shouldValidate: true,
          shouldDirty: true,
        });
        if (patient.patientID || patient.patientId) {
          setValue(
            "patientId",
            String(patient.patientID || patient.patientId),
            {
              shouldValidate: true,
              shouldDirty: true,
            },
          );
        }
        const email = patient?.email || "";
        const contacts = Array.isArray(patient?.contacts)
          ? patient.contacts
          : [];
        const mobile =
          contacts.find((c: any) => String(c?.contactType) === "4" && c?.value)
            ?.value ||
          contacts.find((c: any) => String(c?.contactType) === "2" && c?.value)
            ?.value ||
          "";
        setValue("email", email, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue("mobile", mobile, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }

      setPatientSearchQuery(patient.patientName);
      setShowPatientResults(false);
    },
    [isEditMode, setValue],
  );

  React.useEffect(() => {
    if (
      open &&
      currentBooking &&
      servicePharmacists.length > 0 &&
      !watch("pharmacist")
    ) {
      const bookingAny = currentBooking as any;
      const bookingResourceId = String(
        bookingAny?.resourceId ||
          bookingAny?.ResourceId ||
          bookingAny?.pharmacistId ||
          "",
      )
        .trim()
        .toLowerCase();
      const bookingPharmacist = String(bookingAny?.pharmacist || "")
        .trim()
        .toLowerCase();

      const match = servicePharmacists.find((p: any) => {
        const pId = String(p?.id || "")
          .trim()
          .toLowerCase();
        const pName = String(p?.name || "")
          .trim()
          .toLowerCase();
        return (
          (bookingResourceId && pId === bookingResourceId) ||
          (bookingPharmacist && pName === bookingPharmacist)
        );
      });

      if (match?.id) {
        setValue("pharmacist", String(match.id), {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: true,
        });
      }
      const serviceId = watch("serviceType");
      const matchedId = String(match?.id || "");
      if (serviceId && matchedId && onPharmacistSelected) {
        onPharmacistSelected(serviceId, matchedId)
          .then(async () => {
            // After availability is loaded, fetch rooms
            const formData = getValues();
            await handleGetAppointmentRooms?.(formData);
          })
          .catch(() => {});
      }
    }
  }, [open, currentBooking, servicePharmacists, onPharmacistSelected]);

  // Sync patient search query with form value
  React.useEffect(() => {
    const patientName = watch("patientName") || "";
    setPatientSearchQuery(patientName);
  }, [watch("patientName")]);

  // Cache filtered patients
  React.useEffect(() => {
    if (filteredPatients && filteredPatients.length > 0) {
      setCachedPatients(filteredPatients);
    }
  }, [filteredPatients]);

  // Reset patient search when pharmacy changes
  React.useEffect(() => {
    setCachedPatients([]);
    setLastApiKeyword("");
    setFilteredPatients([]);
  }, [currentPharmacyId]);

  // Bind rooms returned from API endpoint (serviceRooms) based on service type & time selection
  const allAvailableRooms = React.useMemo(() => {
    const list: { id: string; name: string }[] = [];
    const seen = new Set<string>();

    const addRoom = (id: string | number, name: string) => {
      if (!id && !name) return;
      const idStr = String(id || name);
      const nameStr = String(name || id);
      const key = idStr.toLowerCase().trim();
      if (!seen.has(key)) {
        seen.add(key);
        list.push({ id: idStr, name: nameStr });
      }
    };

    // 1. Add serviceRooms (returned from the API endpoint based on service type & time)
    if (Array.isArray(serviceRooms)) {
      serviceRooms.forEach((r: any) => {
        if (r && (r.id || r._id || r.name)) {
          addRoom(r.id || r._id, r.name || r.id || r._id);
        }
      });
    }

    // 2. In edit mode, ensure current booking's room is present so it populates correctly
    if (currentBooking) {
      const b = currentBooking as any;
      const rawRoomVal = b.roomId || b.RoomId || b.room;
      if (rawRoomVal) {
        const matched =
          (pharmacyRooms as any[])?.find(
            (r: any) => String(r.id || r._id) === String(rawRoomVal),
          ) ||
          (serviceRooms as any[])?.find(
            (r: any) => String(r.id || r._id) === String(rawRoomVal),
          );

        const rId = String(rawRoomVal);
        const rName =
          matched?.name ||
          b.roomName ||
          (matched ? matched.name : b.room || rId);
        addRoom(rId, rName);
      }
    }

    return list;
  }, [serviceRooms, currentBooking, pharmacyRooms]);

  // Helper to resolve room ID from booking data matching against allAvailableRooms / pharmacyRooms / serviceRooms
  const resolveRoomId = React.useCallback(
    (bookingData: any): string => {
      if (!bookingData) return "";
      const rawVal =
        bookingData.roomId ||
        bookingData.RoomId ||
        bookingData.room ||
        "";
      if (!rawVal) return "";

      const rawStr = String(rawVal).trim();

      // 1. Try to find match by id/_id in allAvailableRooms
      const matchById = allAvailableRooms.find(
        (r) => String(r.id).toLowerCase() === rawStr.toLowerCase(),
      );
      if (matchById) return matchById.id;

      // 2. Try to find match by name in allAvailableRooms
      const matchByName = allAvailableRooms.find(
        (r) => String(r.name).toLowerCase().trim() === rawStr.toLowerCase(),
      );
      if (matchByName) return matchByName.id;

      // 3. Fallback to pharmacyRooms / serviceRooms
      const pharmMatch =
        (pharmacyRooms as any[])?.find(
          (r: any) =>
            String(r.id || r._id).toLowerCase() === rawStr.toLowerCase() ||
            String(r.name || "").toLowerCase().trim() === rawStr.toLowerCase(),
        ) ||
        (serviceRooms as any[])?.find(
          (r: any) =>
            String(r.id || r._id).toLowerCase() === rawStr.toLowerCase() ||
            String(r.name || "").toLowerCase().trim() === rawStr.toLowerCase(),
        );

      if (pharmMatch) {
        return String(pharmMatch.id || pharmMatch._id);
      }

      return rawStr;
    },
    [allAvailableRooms, pharmacyRooms, serviceRooms],
  );

  // Reset form when modal opens and set values based on edit mode
  React.useEffect(() => {
    if (open) {
      if (currentBooking) {
        // const pharmacistId =
        //   servicePharmacists?.find(
        //     (p: any) => p.name === currentBooking.pharmacist
        //   )?.id || "";
        const hasPatientId = Boolean(
          (currentBooking as any).patientId ||
          (currentBooking as any).patientID,
        );
        const resolvedRoomId = resolveRoomId(currentBooking);

        reset({
          // If we have a patientId, leave name blank so it will be populated from API to avoid incorrect titles/notes
          patientName: hasPatientId
            ? ""
            : (currentBooking as any).patientName || "",
          patientId:
            (currentBooking as any).patientId ||
            (currentBooking as any).patientID ||
            "",
          pharmacist: "",
          serviceType:
            (currentBooking as any).serviceId ||
            (currentBooking as any).serviceType ||
            "",
          date: currentBooking.date,
          time: currentBooking.time,
          duration: String(currentBooking.duration || ""),
          room: resolvedRoomId,
          notes: (currentBooking as any).notes || "",
          email: (currentBooking as any).email || "",
          mobile:
            (currentBooking as any).contactNumber ||
            (currentBooking as any).mobile ||
            "",
          confirmationMethod:
            (currentBooking as any).confirmationMethod ||
            ((currentBooking as any).email
              ? "email"
              : (currentBooking as any).contactNumber
                ? "mobile"
                : undefined),
        });
      } else {
        reset({
          duration: "30",
          date: initialDate || "",
          time: initialTime || "",
          patientName: initialPatientName || "",
          patientId: initialPatientId || "",
          pharmacist: "",
          serviceType: initialServiceTypeId || "",
          room: "",
          notes: "",
          email: initialPatientEmail || "",
          mobile: initialPatientPhone || "",
          confirmationMethod: undefined,
        });
        setLocalTasks([]);
        setLocalVaccines([]);
        setInternalTaskLabel("");
        setInternalVaccineId("");
      }
    }
  }, [
    open,
    currentBooking,
    initialDate,
    initialTime,
    initialServiceTypeId,
    reset,
  ]);

  // Ensure room ID is bound when rooms are loaded or updated
  React.useEffect(() => {
    if (open && currentBooking) {
      const targetRoomId = resolveRoomId(currentBooking);
      if (targetRoomId) {
        const currentRoomInForm = watch("room");
        if (currentRoomInForm !== targetRoomId) {
          setValue("room", targetRoomId, {
            shouldDirty: false,
            shouldTouch: false,
            shouldValidate: true,
          });
        }
      }
    }
  }, [open, currentBooking, resolveRoomId, setValue, watch]);

  // Ensure pharmacist list is fetched when a service is already set (e.g., edit mode)
  React.useEffect(() => {
    if (open) {
      const svc = watch("serviceType");
      if (svc) {
        onServiceTypeSelected?.(svc);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  React.useEffect(() => {
    const rawPid =
      (currentBooking as any)?.patientId || (currentBooking as any)?.patientID;
    if (open && currentBooking && rawPid) {
      const pid = String(rawPid);
      getPatientByPatientId(pid)
        .then((res: any) => {
          const patient = (res && (res.patient || res)) || {};
          const name = (patient as any).patientName || res?.patientName || "";
          if (name) setValue("patientName", name);

          const emailFromPatient = (patient as any)?.email || res?.email || "";
          const contacts = Array.isArray((patient as any)?.contacts)
            ? (patient as any).contacts
            : Array.isArray((res as any)?.contacts)
              ? (res as any).contacts
              : [];
          const mobileFromPatient =
            contacts.find(
              (c: any) => String(c?.contactType) === "4" && c?.value,
            )?.value ||
            contacts.find(
              (c: any) => String(c?.contactType) === "2" && c?.value,
            )?.value ||
            "";

          const currentEmail = (getValues("email") || "").trim();
          const currentMobile = (getValues("mobile") || "").trim();

          if (!currentEmail && emailFromPatient) {
            setValue("email", emailFromPatient, {
              shouldValidate: true,
              shouldDirty: true,
            });
          }
          if (!currentMobile && mobileFromPatient) {
            setValue("mobile", mobileFromPatient, {
              shouldValidate: true,
              shouldDirty: true,
            });
          }
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentBooking]);
  React.useEffect(() => {
    if (!open) return;
    // Only load from consultation actions if editing existing booking
    if (!isEditMode || !currentBooking) return;
    const actions = selectedPatientConsultation?.actions;
    if (!Array.isArray(actions) || actions.length === 0) return;

    const tasks: AppointmentAction[] = [];
    const vaccines: AppointmentVaccine[] = [];

    actions.forEach((action) => {
      try {
        const payload =
          typeof action.payload === "string"
            ? JSON.parse(action.payload)
            : action.payload;

        if (action?.actionNumber === "14" && Array.isArray(payload?.tasks)) {
          payload.tasks.forEach((t: any) => {
            tasks.push({
              id: uuidv4(),
              label: t.label || "",
              completed: t.completed ?? false,
            });
          });
        }

        if (
          action?.actionNumber === "12" &&
          Array.isArray(payload?.vaccineItems)
        ) {
          payload.vaccineItems.forEach((v: any) => {
            vaccines.push({
              id: uuidv4(),
              name: v.vaccineName || "",
              vaccineName: v.vaccineName || "",
              dose: v.schedule || "",
              notes: `${v.route || ""} | ${v.site || ""}`,
            });
          });
        }
      } catch (e) {
        console.error("action parse error", e);
      }
    });

    if (tasks.length > 0) setLocalTasks(tasks);
    if (vaccines.length > 0) setLocalVaccines(vaccines);
  }, [open, selectedPatientConsultation, isEditMode, currentBooking]);
  // Prefill patient from initial props (e.g., from appointment)
  React.useEffect(() => {
    if (!open) return;
    if (isEditMode) return; // Only for new appointments

    // Set patient name
    if (initialPatientName) {
      const currentPatientName = (watch("patientName") || "").trim();
      if (!currentPatientName) {
        setValue("patientName", initialPatientName, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setPatientSearchQuery(initialPatientName);
      }
    }

    if (initialPatientId) {
      const currentPatientId = (watch("patientId") || "").trim();
      if (!currentPatientId) {
        setValue("patientId", initialPatientId, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    }

    if (initialPatientEmail) {
      const currentEmail = (watch("email") || "").trim();
      if (!currentEmail) {
        setValue("email", initialPatientEmail, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    }

    if (initialPatientPhone) {
      const currentMobile = (watch("mobile") || "").trim();
      if (!currentMobile) {
        setValue("mobile", initialPatientPhone, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    }
  }, [open, isEditMode, initialPatientName, setValue]);

  // Prefill pharmacist from MiniCalendar filters if provided
  React.useEffect(() => {
    if (!open) return;
    if (!initialPharmacistName) return;
    const currentVal = (watch("pharmacist") || "").trim();
    if (currentVal) return;
    const match = (servicePharmacists || []).find(
      (p: any) => String(p?.name) === String(initialPharmacistName),
    );
    if (match && match.id) {
      setValue("pharmacist", String(match.id), {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialPharmacistName, servicePharmacists]);

  const serviceType = watch("serviceType");
  const selectedDate = watch("date") || "";
  const selectedTime = watch("time") || "";
  const selectedPharmacist = watch("pharmacist") || "";
  const [dateError, setDateError] = React.useState<string | null>(null);
  const [availabilityError, setAvailabilityError] = React.useState<
    string | null
  >(null);

  const serviceOptionsForSelect = React.useMemo(() => {
    const base = (mappedServices || []).filter(
      (service: any) =>
        service?.serviceType !== 1 && service?.serviceType !== 3,
    );

    const currentServiceId = String(
      serviceType || (currentBooking as any)?.serviceId || "",
    ).trim();

    if (!currentServiceId) return base;
    if (base.some((service: any) => String(service?._id) === currentServiceId)) {
      return base;
    }

    const selectedFromAllRaw = (
      defaultCalenderAppointmentPharmacyServices || []
    ).find((service: any) => String(service?._id) === currentServiceId);

    const selectedFromAll = selectedFromAllRaw
      ? {
          _id: selectedFromAllRaw._id,
          name: selectedFromAllRaw.serviceName,
          serviceType: selectedFromAllRaw.serviceType,
          serviceDuration: selectedFromAllRaw.serviceDuration,
          isRoomRequired: selectedFromAllRaw.isRoomRequired,
        }
      : undefined;

    const selectedFromBooking = !selectedFromAll
      ? {
          _id: currentServiceId,
          name: String((currentBooking as any)?.serviceName || "Selected Service").trim(),
          serviceType: (currentBooking as any)?.serviceType,
          serviceDuration: Number((currentBooking as any)?.duration || 0),
          isRoomRequired: false,
        }
      : undefined;

    if (selectedFromAll) return [selectedFromAll, ...base];
    if (selectedFromBooking) return [selectedFromBooking, ...base];
    return base;
  }, [
    mappedServices,
    serviceType,
    currentBooking,
    defaultCalenderAppointmentPharmacyServices,
  ]);

  const availableTimes = React.useMemo(() => {
    let times: string[] = [];
     if (selectedPharmacist  || selectedPharmacist === "any") {
      times = getAvailableTimesForDate
        ? getAvailableTimesForDate(selectedDate)
        : [];
    } else if (getAggregatedTimesForDate) {
      // If no pharmacist selected, show slots based on service duration across all users
      const durationVal = parseInt(watch("duration") || "30", 10);
      times = getAggregatedTimesForDate(selectedDate, durationVal);
    }

    if (currentBooking?.time && !times.includes(currentBooking.time)) {
      times.push(currentBooking.time);
    }
    return times.sort();
  }, [
    getAvailableTimesForDate,
    getAggregatedTimesForDate,
    selectedDate,
    currentBooking?.time,
    selectedPharmacist,
    watch("duration"),
  ]);

  const disabledDate = React.useCallback(
    (d: Date) => {
      const key = formatDateFromDate
        ? formatDateFromDate(d)
        : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
            2,
            "0",
          )}-${String(d.getDate()).padStart(2, "0")}`;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const d0 = new Date(d);
      d0.setHours(0, 0, 0, 0);
      return d0 < today || !availableDates?.includes(key);
    },
    [availableDates, formatDateFromDate],
  );

  // Clear date/time if not compatible with availability
  React.useEffect(() => {
    if (isEditMode) return;
    if (selectedDate && !availableDates?.includes(selectedDate)) {
      setValue("date", "");
      setValue("time", "");
    }
  }, [availableDates]);

  // Show validation error when pharmacist has no availability
  React.useEffect(() => {
    if (currentBooking) {
      setAvailabilityError(null);
      return;
    }
    if (!selectedPharmacist || !serviceType) {
      setAvailabilityError(null);
      return;
    }
    if (selectedPharmacist === "any") {
      setAvailabilityError(null);
      return;
    }
    const isNewlyCreated = isEditMode && currentBooking;
    if (isNewlyCreated) {
      setAvailabilityError(null);
      return;
    }
    if (Array.isArray(availableDates) && availableDates.length === 0) {
      setAvailabilityError("This user has no available dates or time slots.");
      setValue("date", "");
      setValue("time", "");
    } else {
      setAvailabilityError(null);
    }
  }, [
    availableDates,
    selectedPharmacist,
    serviceType,
    isEditMode,
    currentBooking,
  ]);
  React.useEffect(() => {
    if (currentBooking) return;

    if (
      selectedTime &&
      availableTimes.length > 0 &&
      !availableTimes.includes(selectedTime)
    ) {
      setValue("time", "");
    }
  }, [availableTimes, selectedTime, currentBooking]);

  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const onSubmit = React.useCallback(
    async (data: BookingForm) => {
      setSubmitError(null);

      try {
        // append task/vaccine lists before sending to parent
        // tasks are objects locally but backend expects string list
        const taskStrings = localTasks.map((t) => t.label);
        const payload: any = {
          ...data,
          ...(taskStrings.length ? { tasksToComplete: taskStrings } : {}),
          ...(localVaccines.length
            ? { vaccinesAdminister: localVaccines }
            : {}),
        };

        if (
          !validateTemplateAvailability(
            data,
            confirmCancelTemplates,
            setValue,
            setError,
            isEditMode ? "update" : "insert",
          )
        ) {
          return;
        }

        // const validationResult = validatePharmacySlots(data);
        // if (!validationResult.isValid) {
        //   setSubmitError(validationResult.message);
        //   return;
        // }
        const validationResult = validateBookingConflicts(
          data,
          currentBooking?.id,
        );
        if (!validationResult.isValid) {
          setSubmitError(validationResult.message);
          return;
        }
        await Promise.resolve(onSave(payload));

        if (bookingSlotsMode === "fromAppointment") {
          const patientIdForRefresh = String(
            data.patientId ||
              selectedPatientId ||
              (currentBooking as any)?.patientId ||
              (currentBooking as any)?.patientID ||
              "",
          ).trim();

          if (patientIdForRefresh) {
            await getPatientAppointmentsByPatientId(patientIdForRefresh);
          }
        }

        if (currentUser && currentUser.emailAddress) {
          trackFirstTimeIntercomEvent({
            eventName: "calendar_appointment_first_time",
            user: {
              id: currentUser.emailAddress,
              email: currentUser.emailAddress,
              name: currentUser.fullName || "",
            },
            properties: {
              source: "marketplace_settings",
            },
          });
        }
        // Close only on success
        setOpen(false);
        if (!isEditMode) {
          reset();
          // clear the local lists as well when creating a fresh booking
          setLocalTasks([]);
          setLocalVaccines([]);
        }
      } catch (e: any) {
        const message =
          (e && (e.message || e.toString())) ||
          "Unable to create appointment. Please review the fields.";
        setSubmitError(message);
      }
    },
    [
      onSave,
      isEditMode,
      reset,
      setOpen,
      currentUser,
      localTasks,
      localVaccines,
    ],
  );

  const watchedDate = watch("date");
  const watchedTime = watch("time");
  const watchedDuration = watch("duration");
  const watchedPharmacist = watch("pharmacist");
  const watchedRoom = watch("room");

  React.useEffect(() => {
    if (submitError) setSubmitError(null);
  }, [watchedDate, watchedTime, watchedDuration, watchedPharmacist, watchedRoom]);

  // Clear inline error when modal closes
  React.useEffect(() => {
    if (!open) {
      setSubmitError(null);
    }
  }, [open]);

  React.useEffect(() => {
    if (!open) {
      setAvailabilityError(null);
      setDateError(null);
      setValue("date", "");
      setValue("time", "");
      setFilteredPatients([]);
      // Reset partial match state when modal closes
      setLocalPartialMatch(false);
      setLocalMatchingPatients([]);
      // Clear all task and vaccine state when modal closes
      setLocalTasks([]);
      setLocalVaccines([]);
      setInternalTaskLabel("");
      setInternalVaccineId("");
    }
  }, [open, setValue]);

  React.useEffect(() => {}, [
    localPartialMatch,
    showPatientResults,
    showQuickAddPatient,
    patientSearchQuery,
    localMatchingPatients,
  ]);

  const currentDuration = watch("duration");
  const durations = React.useMemo(() => {
    const list = [...(serviceDurations || [])];
    if (
      currentBooking?.duration &&
      !list.some((d) => d.value === String(currentBooking.duration))
    ) {
      list.push({
        value: String(currentBooking.duration),
        label: `${currentBooking.duration} minutes`,
      });
    }
    if (
      currentDuration &&
      !list.some((d) => d.value === String(currentDuration))
    ) {
      list.push({
        value: String(currentDuration),
        label: `${currentDuration} minutes`,
      });
    }

    return list;
  }, [currentDuration, currentBooking, serviceDurations]);

  const todayStr = React.useMemo(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }, []);

  const handleBackToSlots = React.useCallback(() => {
    setOpen(false);
    setShowBookingSlots(true);
  }, []);

  const bookingData = currentBooking as any;

  const hasPatientId = !!bookingData?.patientId;

  const isStartConsultationDisabled =
    isEventCalendarLoading ||
    isPatientDetailsLoading ||
    [1, 4].includes(bookingData?.eventStatus) ||
    isConsultationCompleted;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent
        className={`sm:max-w-[600px] transition-colors duration-300 max-h-[100vh] overflow-y-auto ${
          isDarkMode
            ? "bg-gray-800 border-gray-600 text-white"
            : "bg-white border-gray-200 text-gray-900"
        }`}
      >
        <DialogHeader className={`flex-shrink-0`}>
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {showBacktoSlotsButton && !showBookingSlots && !isEditMode && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleBackToSlots}
                  className={`p-2 ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"}`}
                >
                  <ArrowLeft className="w-4 h-4" />
                </Button>
              )}
              <DialogTitle
                className={`text-lg font-semibold transition-colors duration-300 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                {isEditMode
                  ? "Edit Appointment"
                  : showBookingSlots
                    ? "Select Time Slot"
                    : "New Appointment"}
              </DialogTitle>
            </div>
          </div>
        </DialogHeader>

        {availabilityError && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="w-4 h-4" />
            <AlertTitle>Not available</AlertTitle>
            <AlertDescription>
              This pharmacist has no available dates or time slots. Please
              choose a different pharmacist or service.
            </AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <Accordion
            type="multiple"
            defaultValue={["essential-info", "notifications"]}
            className={`w-full border-t transition-colors duration-300 ${
              isDarkMode ? "border-gray-600" : "border-gray-200"
            }`}
          >
            <AccordionItem
              value="essential-info"
              className={`border-b transition-colors duration-300 ${isDarkMode ? "border-gray-600" : "border-gray-200"}`}
            >
              <AccordionTrigger
                className={`px-0 py-4 ${isDarkMode ? "hover:bg-gray-700/50" : "hover:bg-gray-50"}`}
              >
                <div className="flex items-center gap-2 font-semibold">
                  <Calendar className="w-5 h-5" />
                  <span>Essential Information</span>
                  {isCancelledAppointment && (
                    <span
                      className={`px-2 py-0.5 text-xs font-semibold rounded ${
                        isDarkMode
                          ? "bg-red-900/40 text-red-200 border border-red-700"
                          : "bg-red-100 text-red-700 border border-red-200"
                      }`}
                    >
                      (Cancelled)
                    </span>
                  )}
                  {/* {selectedPatient && (
              <Badge className="ml-2" variant="secondary">
                {selectedPatient.name}
              </Badge>
            )} */}
                </div>
              </AccordionTrigger>
              {showQuickAddPatient ? (
                <InlineQuickAddPatient
                  isDarkMode={isDarkMode}
                  defaultValues={quickAddDefaults}
                  onCancel={() => {
                    setShowQuickAddPatient(false);
                    // Clear search query when canceling
                    setPatientSearchQuery("");
                  }}
                  onSave={async (formData) => {
                    // Call handleSavePatient with the form data
                    await handleSavePatient(formData);
                    // The partial match state will be set inside handleSavePatient
                  }}
                />
              ) : (
                <AccordionContent className="pt-0 px-2 pb-6 !overflow-visible">
                  <div className="grid grid-cols-2 gap-4">
                    {/* Patient Name (full width) */}
                    <div className="col-span-2 space-y-2">
                      <Label
                        className={`text-sm font-medium transition-colors duration-300 ${
                          isDarkMode ? "text-gray-200" : "text-gray-700"
                        }
              `}
                      >
                        Patient Name *
                      </Label>
                      <div className="relative w-full">
                        {/* Search Icon */}
                        <Search
                          className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 transition-colors duration-300 ${
                            isDarkMode ? "text-gray-400" : "text-gray-400"
                          } pointer-events-none`}
                        />

                        {/* Input */}
                        <Input
                          placeholder="Search Patient"
                          value={patientSearchQuery}
                          onChange={(e) => handlePatientSearch(e.target.value)}
                          onFocus={() => {
                            if (
                              patientSearchQuery.length > 0 &&
                              sortedPatients?.length > 0
                            ) {
                              setShowPatientResults(true);
                            }
                          }}
                          onBlur={() => {
                            setTimeout(() => setShowPatientResults(false), 200);
                          }}
                          disabled={
                            isEventCalendarLoading ||
                            isEditMode ||
                            bookingSlotsMode === "fromAppointment"
                          }
                          maxLength={200}
                          className={`pl-10 transition-colors duration-300 ${
                            isDarkMode
                              ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                              : "bg-white border-gray-200"
                          }`}
                        />

                        {/* Patient Results Dropdown */}
                        {showPatientResults && sortedPatients?.length > 0 && (
                          <div
                            className={`absolute top-full left-0 right-0 mt-1 border rounded-lg text-sm shadow-lg z-50 max-h-80 overflow-y-auto transition-colors duration-300 ${
                              isDarkMode
                                ? "bg-gray-800 border-gray-600"
                                : "bg-white border-gray-200"
                            }`}
                          >
                            {sortedPatients.map((patient: any) => (
                              <div
                                key={patient.id || patient.patientID}
                                onClick={() => handlePatientSelect(patient)}
                                className={`p-3 border-b last:border-b-0 hover:transition-colors duration-200 cursor-pointer ${
                                  isDarkMode
                                    ? "border-gray-700 hover:bg-gray-700"
                                    : "border-gray-100 hover:bg-gray-50"
                                }`}
                              >
                                <div
                                  className={`text-sm font-medium transition-colors duration-300 ${
                                    isDarkMode ? "text-white" : "text-gray-900"
                                  }`}
                                >
                                  {patient.patientName}
                                </div>
                                {(patient.nhsNumber || patient.dob) && (
                                  <div
                                    className={`text-xs transition-colors duration-300 ${
                                      isDarkMode
                                        ? "text-gray-400"
                                        : "text-gray-500"
                                    }`}
                                  >
                                    NHS: {patient.nhsNumber} • DOB:{" "}
                                    {getFormattedDate(patient.dob)}
                                  </div>
                                )}
                                {patient.address && (
                                  <div
                                    className={`text-xs transition-colors duration-300 ${
                                      isDarkMode
                                        ? "text-gray-500"
                                        : "text-gray-400"
                                    }`}
                                  >
                                    {patient.address}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* No Results */}
                        {showPatientResults &&
                          patientSearchQuery.length > 0 &&
                          filteredPatients?.length === 0 && (
                            <div
                              className={`absolute top-full left-0 right-0 mt-1 border rounded-lg shadow-lg z-50 p-4 text-center transition-colors duration-300 ${
                                isDarkMode
                                  ? "bg-gray-800 border-gray-600 text-gray-400"
                                  : "bg-white border-gray-200 text-gray-500"
                              }`}
                            >
                              <Search className="w-6 h-6 mx-auto mb-2 opacity-50" />

                              <p className="text-sm font-medium">
                                No patients found
                              </p>

                              <p className="mt-1 text-xs opacity-75">
                                Try searching by name or NHS number
                              </p>

                              <button
                                type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                  setShowPatientResults(false);
                                  handleAddPatient(patientSearchQuery);
                                }}
                                className="mt-3 w-full px-3 py-2 rounded-md text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white transition-colors"
                              >
                                + Add New Patient
                              </button>
                            </div>
                          )}
                      </div>
                      {errors.patientName && (
                        <p
                          className={`text-sm transition-colors duration-300 ${
                            isDarkMode ? "text-red-400" : "text-red-600"
                          }
                `}
                        >
                          {errors.patientName.message}
                        </p>
                      )}
                    </div>

                    {/* Service Type */}
                    <div className="space-y-2">
                      <Label
                        className={`text-sm font-medium transition-colors duration-300 ${
                          isDarkMode ? "text-gray-200" : "text-gray-700"
                        }
              `}
                      >
                        Service Type *
                      </Label>
                      <Select
                        value={serviceType}
                        onValueChange={async (value: string) => {
                          try {
                            setValue("pharmacist", "");
                            setIsPharmacistLoading(true);
                            setBookingConfirmationLoading(true);
                            await onServiceTypeSelected?.(value);
                            setValue("serviceType", value);

                            // If pharmacist is already selected, we need to re-fetch availability
                            // because the service duration might have changed
                            const currentPharmacistId = getValues("pharmacist");
                            if (currentPharmacistId && onPharmacistSelected) {
                              await onPharmacistSelected(
                                value,
                                currentPharmacistId,
                              );
                            }
                            const formData = getValues();
                            await handleGetAppointmentRooms(formData);
                          } finally {
                            setValue("serviceType", value);
                            setIsPharmacistLoading(false);
                            setBookingConfirmationLoading(false);
                            const svc = (mappedServices || []).find(
                              (s: any) => s?._id === value,
                            );
                            const base =
                              parseInt(
                                String((svc as any)?.serviceDuration ?? 0),
                                10,
                              ) || 0;
                            if (base > 0) setValue("duration", String(base));
                          }
                        }}
                        disabled={
                          isEventCalendarLoading || isConsultationCompleted
                        }
                      >
                        <SelectTrigger
                          className="w-full"
                          disabled={
                            isEventCalendarLoading || isConsultationCompleted
                          }
                        >
                          <SelectValue placeholder="Select service" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem key="" value="">
                            Select Service
                          </SelectItem>
                          {serviceOptionsForSelect.map((service) => (
                              <SelectItem
                                key={service?._id}
                                value={service?._id}
                              >
                                {service?.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      {errors.serviceType && (
                        <p
                          className={`text-sm transition-colors duration-300 ${
                            isDarkMode ? "text-red-400" : "text-red-600"
                          }
                `}
                        >
                          {errors.serviceType.message}
                        </p>
                      )}
                    </div>

                    {/* Pharmacist */}
                    <div className="space-y-2">
                      <Label
                        className={`text-sm font-medium transition-colors duration-300 ${
                          isDarkMode ? "text-gray-200" : "text-gray-700"
                        }
              `}
                      >
                        Pharmacist *
                      </Label>
                      <Select
                        value={watch("pharmacist")}
                        onValueChange={async (value) => {
                          try {
                            setAvailabilityError(null);
                            const svc = watch("serviceType");
                            if (svc && value === "any" && onAnyPharmacistSelected) {
                              await onAnyPharmacistSelected(svc);
                            } else if (
                              svc &&
                              onPharmacistSelected &&
                              value !== "any"
                            ) {
                              await onPharmacistSelected(svc, value);
                            }
                          } finally {
                            setValue("pharmacist", value);
                            const formData = getValues();
                            await handleGetAppointmentRooms(formData);
                          }
                        }}
                        disabled={
                          isEventCalendarLoading ||
                          isConsultationCompleted ||
                          isPharmacistLoading
                        }
                      >
                        <SelectTrigger
                          className="w-full"
                          disabled={
                            isEventCalendarLoading ||
                            isConsultationCompleted ||
                            isPharmacistLoading
                          }
                        >
                          <SelectValue placeholder="Select pharmacist" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem key="" value="">
                            Select pharmacist
                          </SelectItem>
                          {!isPharmacistLoading &&
                            servicePharmacists.length > 1 && (
                              <SelectItem
                                key={"any"}
                                value={"any"}
                              >
                                Any Pharmacist
                              </SelectItem>
                            )}
                          {!isPharmacistLoading &&
                            servicePharmacists.map((pharmacist) => (
                              <SelectItem
                                key={pharmacist?.id}
                                value={pharmacist?.id}
                              >
                                {pharmacist?.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      {errors.pharmacist && (
                        <p
                          className={`text-sm transition-colors duration-300 ${
                            isDarkMode ? "text-red-400" : "text-red-600"
                          }
                `}
                        >
                          {errors.pharmacist.message}
                        </p>
                      )}
                    </div>

                    {/* Date */}
                    <div className="space-y-2">
                      <Label
                        className={`text-sm font-medium transition-colors duration-300 ${
                          isDarkMode ? "text-gray-200" : "text-gray-700"
                        }
              `}
                      >
                        Date *
                      </Label>
                      <Input
                        id="date"
                        type="date"
                        value={selectedDate}
                        onChange={async (e) => {
                          const v = (e.target as HTMLInputElement).value;

                          setValue("date", v);
                          const formData = getValues();
                          await handleGetAppointmentRooms(formData);

                          const today = new Date();
                          const todayStr = `${today.getFullYear()}-${String(
                            today.getMonth() + 1,
                          ).padStart(2, "0")}-${String(
                            today.getDate(),
                          ).padStart(2, "0")}`;
                          if (!availableDates?.includes(v) || v < todayStr) {
                            setDateError(
                              "This date has no available slots for the selected pharmacist and service.",
                            );
                            setValue("date", "");
                            setValue("time", "");
                            return;
                          }
                          setDateError(null);
                          setValue("date", v, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                          const times = getAvailableTimesForDate
                            ? getAvailableTimesForDate(v)
                            : [];
                          if (!times.includes(watch("time") || "")) {
                            setValue("time", "");
                          }
                        }}
                        disabled={
                          isEventCalendarLoading ||
                          !availableDates?.length ||
                          isConsultationCompleted
                        }
                        min={todayStr}
                        className="w-full"
                      />
                      {errors.date && (
                        <p
                          className={`text-sm transition-colors duration-300 ${
                            isDarkMode ? "text-red-400" : "text-red-600"
                          }
                `}
                        >
                          {errors.date.message}
                        </p>
                      )}
                      {dateError && (
                        <p
                          className={`text-sm mt-1 ${
                            isDarkMode ? "text-red-400" : "text-red-600"
                          }`}
                        >
                          {dateError}
                        </p>
                      )}
                    </div>

                    {/* Time */}
                    <div className="space-y-2">
                      <Label
                        className={`text-sm font-medium transition-colors duration-300 ${
                          isDarkMode ? "text-gray-200" : "text-gray-700"
                        }
              `}
                      >
                        Time *
                      </Label>
                      <Select
                        value={selectedTime}
                        onValueChange={async (value) => {
                          setIsRoomsLoading(true);
                          setValue("time", value);
                          const formData = getValues();
                          await handleGetAppointmentRooms(formData);
                          setIsRoomsLoading(false);
                        }}
                        disabled={
                          isConsultationCompleted ||
                          isEventCalendarLoading ||
                          !selectedDate ||
                          availableTimes.length === 0
                        }
                      >
                        <SelectTrigger
                          className="w-full"
                          disabled={
                            isEventCalendarLoading || isConsultationCompleted
                          }
                        >
                          <SelectValue placeholder="Select time" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem key="" value="">
                            Select time
                          </SelectItem>
                          {availableTimes.map((time) => (
                            <SelectItem key={time} value={time}>
                              {time}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.time && (
                        <p
                          className={`text-sm transition-colors duration-300 ${
                            isDarkMode ? "text-red-400" : "text-red-600"
                          }
                `}
                        >
                          {errors.time.message}
                        </p>
                      )}
                    </div>

                    {/* Duration */}
                    <div className="space-y-2">
                      <Label
                        className={`text-sm font-medium transition-colors duration-300 ${
                          isDarkMode ? "text-gray-200" : "text-gray-700"
                        }`}
                      >
                        Duration *
                      </Label>
                      <Select
                        value={watch("duration")}
                        onValueChange={async (value) => {
                          setValue("duration", value);
                          const formData = getValues();
                          await handleGetAppointmentRooms(formData);
                        }}
                        disabled={
                          isEventCalendarLoading || isConsultationCompleted
                        }
                      >
                        <SelectTrigger
                          className="w-full"
                          disabled={
                            isEventCalendarLoading || isConsultationCompleted
                          }
                        >
                          <SelectValue placeholder="Select duration" />
                        </SelectTrigger>
                        <SelectContent>
                          {durations.map((duration) => (
                            <SelectItem
                              key={duration.value}
                              value={duration.value}
                            >
                              {duration.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.duration && (
                        <p
                          className={`text-sm transition-colors duration-300 ${
                            isDarkMode ? "text-red-400" : "text-red-600"
                          }`}
                        >
                          {errors.duration.message}
                        </p>
                      )}
                    </div>

                    {/* Room */}
                    <div className="space-y-2">
                      <Label
                        className={`text-sm font-medium transition-colors duration-300 ${
                          isDarkMode ? "text-gray-200" : "text-gray-700"
                        }`}
                      >
                        {(() => {
                          const svc = (mappedServices || []).find(
                            (s: any) => s?._id === serviceType,
                          );
                          return svc && (svc as any).isRoomRequired
                            ? "Room *"
                            : "Room";
                        })()}
                      </Label>
                      <Select
                        value={watch("room") || "none"}
                        onValueChange={(value) =>
                          setValue("room", value === "none" ? "" : value)
                        }
                        disabled={
                          isEventCalendarLoading ||
                          isConsultationCompleted ||
                          isRoomsLoading
                        }
                      >
                        <SelectTrigger
                          className="w-full"
                          disabled={
                            isEventCalendarLoading ||
                            isConsultationCompleted ||
                            isRoomsLoading
                          }
                        >
                          <SelectValue
                            placeholder={(() => {
                              const svc = (mappedServices || []).find(
                                (s: any) => s?._id === serviceType,
                              );
                              return svc && (svc as any).isRoomRequired
                                ? "Select room"
                                : "Select room (optional)";
                            })()}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No room assigned</SelectItem>
                          {!isRoomsLoading &&
                            allAvailableRooms.map((room: any) => (
                              <SelectItem key={room.id} value={room.id}>
                                {room.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      {errors.room && (
                        <p
                          className={`text-sm transition-colors duration-300 ${
                            isDarkMode ? "text-red-400" : "text-red-600"
                          }`}
                        >
                          {errors.room.message}
                        </p>
                      )}
                    </div>
                  </div>
                </AccordionContent>
              )}
            </AccordionItem>

            <AccordionItem
              value="notifications"
              className={`border-b transition-colors duration-300 ${isDarkMode ? "border-gray-600" : "border-gray-200"}`}
            >
              <AccordionTrigger
                className={`px-0 py-4 ${isDarkMode ? "hover:bg-gray-700/50" : "hover:bg-gray-50"}`}
              >
                <div className="flex items-center gap-2 font-semibold">
                  <Bell className="w-5 h-5" />
                  <span>Notifications</span>
                  {watch("confirmationMethod") && (
                    <span
                      className={`text-xs font-normal ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      (
                      {watch("confirmationMethod") === "email"
                        ? "Email"
                        : watch("confirmationMethod") === "mobile"
                          ? "SMS"
                          : "Do not send"}
                      )
                    </span>
                  )}
                  {!watch("confirmationMethod") && (
                    <span
                      className={`text-xs font-normal ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      (Do not send)
                    </span>
                  )}
                </div>
              </AccordionTrigger>

              <AccordionContent className="pt-0 px-2 pb-6 !overflow-visible">
                {/* Contact Information */}
                <div className="col-span-2 space-y-2">
                  <Label
                    className={`text-sm font-medium transition-colors duration-300 ${
                      isDarkMode ? "text-gray-200" : "text-gray-700"
                    }`}
                  >
                    Contact Information
                  </Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label
                        className={`text-xs ${
                          isDarkMode ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        Email
                      </Label>
                      <Input
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        maxLength={254}
                        placeholder="name@email.com"
                        pattern="^[^\s@]+@[^\s@]+\.[^\s@]+$"
                        disabled={isConsultationCompleted}
                        {...register("email", {
                          onChange: (e) => {
                            const val = (
                              e.target as HTMLInputElement
                            ).value.replace(/\s+/g, "");
                            setValue("email", val, {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                          },
                          onBlur: (e) => {
                            setValue(
                              "email",
                              (e.target as HTMLInputElement).value.trim(),
                              { shouldValidate: true },
                            );
                          },
                        })}
                        className={`${
                          isDarkMode
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                            : "bg-white border-gray-200"
                        } ${
                          errors.email
                            ? "border-red-500 focus-visible:ring-red-500"
                            : ""
                        }`}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label
                        className={`text-xs ${
                          isDarkMode ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        Mobile
                      </Label>
                      <Input
                        type="tel"
                        inputMode="tel"
                        autoComplete="tel"
                        maxLength={20}
                        placeholder="07700 900123"
                        disabled={isConsultationCompleted}
                        pattern="^[0-9+()\-\s]*$"
                        {...register("mobile", {
                          onChange: (e) => {
                            const raw = (e.target as HTMLInputElement).value;
                            const sanitized = raw.replace(/[^0-9+()\-\s]/g, "");
                            setValue("mobile", sanitized, {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                          },
                          onBlur: (e) => {
                            setValue(
                              "mobile",
                              (e.target as HTMLInputElement).value.trim(),
                              { shouldValidate: true },
                            );
                          },
                        })}
                        className={`${
                          isDarkMode
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                            : "bg-white border-gray-200"
                        } ${
                          errors.mobile
                            ? "border-red-500 focus-visible:ring-red-500"
                            : ""
                        }`}
                      />
                    </div>
                  </div>
                </div>

                {/* Confirmation method */}
                <div className="mt-4 space-y-2">
                  <Label
                    className={`text-sm font-medium transition-colors duration-300 ${
                      isDarkMode ? "text-gray-200" : "text-gray-700"
                    }`}
                  >
                    Send booking confirmation via
                  </Label>
                  <RadioGroup
                    value={watch("confirmationMethod") as any}
                    disabled={BookingConfirmationLoading}
                    onValueChange={(v) =>
                      setValue("confirmationMethod", v as any, {
                        shouldValidate: true,
                      })
                    }
                    className="grid grid-cols-2 gap-4"
                  >
                    <div
                      className={`flex items-center gap-2 p-3 rounded-md border ${
                        isDarkMode ? "border-gray-700" : "border-gray-200"
                      }`}
                    >
                      <RadioGroupItem
                        value="email"
                        id="confirm-email"
                        disabled={
                          !watch("email") ||
                          Boolean((errors as any).email) ||
                          isConsultationCompleted
                        }
                      />
                      <Label htmlFor="confirm-email" className="flex-1">
                        Email
                        <span
                          className={`block text-xs mt-0.5 ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          {watch("email") || "No email provided"}
                        </span>
                      </Label>
                    </div>
                    <div
                      className={`flex items-center gap-2 p-3 rounded-md border ${
                        isDarkMode ? "border-gray-700" : "border-gray-200"
                      }`}
                    >
                      <RadioGroupItem
                        value="mobile"
                        id="confirm-mobile"
                        disabled={
                          !watch("mobile") ||
                          Boolean((errors as any).mobile) ||
                          isConsultationCompleted
                        }
                      />
                      <Label htmlFor="confirm-mobile" className="flex-1">
                        Mobile (SMS)
                        <span
                          className={`block text-xs mt-0.5 ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          {watch("mobile") || "No mobile provided"}
                        </span>
                      </Label>
                    </div>
                  </RadioGroup>
                  {errors.confirmationMethod && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.confirmationMethod.message}
                    </p>
                  )}
                </div>

                {/* Event Name */}
                <div className="mt-4 space-y-2">
                  <Label
                    htmlFor="notes"
                    className={`text-sm font-medium transition-colors duration-300 ${
                      isDarkMode ? "text-gray-200" : "text-gray-700"
                    }`}
                  >
                    Notes (optional)
                  </Label>
                  <Input
                    id="notes"
                    type="text"
                    placeholder="Additional notes or instructions"
                    disabled={isEventCalendarLoading || isConsultationCompleted}
                    {...register("notes")}
                    className="w-full"
                  />
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* TASKS TO COMPLETE SECTION */}
            {(localTasks?.length > 0 || !isReadOnly) && (
              <AccordionItem
                value="tasks"
                className={`border-b transition-colors duration-300 ${isDarkMode ? "border-gray-600" : "border-gray-200"}`}
              >
                <AccordionTrigger
                  className={`px-0 py-4 ${isDarkMode ? "hover:bg-gray-700/50" : "hover:bg-gray-50"}`}
                >
                  <div className="flex items-center gap-2 font-semibold">
                    <CheckSquare className="w-5 h-5" />
                    <span>Tasks to Complete</span>
                    {localTasks?.length > 0 && (
                      <span className="ml-2 inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-blue-600 rounded-full">
                        {localTasks.length}
                      </span>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-0 pb-6 !overflow-visible">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      {localTasks?.map((task) => (
                        <div
                          key={task.id}
                          className={`flex items-center justify-between p-3 rounded-lg border ${isDarkMode ? "bg-gray-700 border-gray-600" : "bg-gray-50 border-gray-200"}`}
                        >
                          <span
                            className={`text-sm ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
                          >
                            {task.label}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRemoveTask(task.id)}
                            className={`p-1 rounded hover:${isDarkMode ? "bg-gray-600" : "bg-gray-200"}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Input
                        type="text"
                        placeholder="Add new task"
                        value={taskLabel}
                        onChange={(e) => handleTaskLabelChange(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && taskLabel.trim()) {
                            e.preventDefault();
                            handleAddTask(taskLabel);
                          }
                        }}
                        disabled={isReadOnly}
                        className={`${isDarkMode ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400" : "bg-white border-gray-200"}`}
                      />
                      <Button
                        type="button"
                        onClick={() => handleAddTask(taskLabel)}
                        disabled={isReadOnly || !taskLabel.trim()}
                        size="sm"
                        className="gap-1"
                      >
                        <Plus className="w-4 h-4" />
                        Add
                      </Button>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}

            {/* VACCINES TO ADMINISTER SECTION */}
            {(localVaccines.length > 0 || !isReadOnly) && (
              <AccordionItem
                value="vaccines"
                className={`border-b transition-colors duration-300 ${isDarkMode ? "border-gray-600" : "border-gray-200"}`}
              >
                <AccordionTrigger
                  className={`px-0 py-4 ${isDarkMode ? "hover:bg-gray-700/50" : "hover:bg-gray-50"}`}
                >
                  <div className="flex items-center gap-2 font-semibold">
                    <Syringe className="w-5 h-5" />
                    <span>Vaccines to Administer</span>
                    {localVaccines.length > 0 && (
                      <span className="ml-2 inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-purple-600 rounded-full">
                        {localVaccines.length}
                      </span>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-0 pb-6 !overflow-visible">
                  <div className="space-y-4">
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {localVaccines.map((vaccine) => (
                        <div
                          key={vaccine.id}
                          className={`flex items-start justify-between p-3 rounded-lg border ${isDarkMode ? "bg-gray-700 border-gray-600" : "bg-gray-50 border-gray-200"}`}
                        >
                          <div className="flex-1 min-w-0">
                            <p
                              className={`text-sm font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
                            >
                              {vaccine.name}
                            </p>
                            {vaccine.dose && (
                              <p
                                className={`text-xs mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                              >
                                {vaccine.dose}
                              </p>
                            )}
                            {vaccine.notes && (
                              <p
                                className={`text-xs mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                              >
                                {vaccine.notes}
                              </p>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveVaccine(vaccine.id)}
                            className={`p-1 rounded hover:${isDarkMode ? "bg_gray-600" : "bg-gray-200"} ml-2 flex-shrink-0`}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="space-y-2 pt-2">
                      <VaccineCombobox
                        value={vaccineIdState}
                        onChange={handleVaccineIdChange}
                        placeholder="Search vaccine..."
                        isDarkMode={isDarkMode}
                        disabled={isReadOnly}
                        onEnterKey={() => {
                          if (vaccineIdState.trim()) {
                            handleAddVaccine(vaccineIdState);
                          }
                        }}
                        onSelect={(id) => {
                          handleVaccineIdChange(id);
                          handleAddVaccine(id);
                        }}
                      />
                      {/* <Button
                        type="button"
                        onClick={() => {
                          if (vaccineIdState.trim()) {
                            handleAddVaccine(vaccineIdState);
                          }
                        }}
                        disabled={isReadOnly || !vaccineIdState.trim()}
                        size="sm"
                        className="w-full gap-1"
                      >
                        <Plus className="w-4 h-4" />
                        Add Vaccine
                      </Button> */}
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}
          </Accordion>

          {/* Inline form error (e.g., duplicate/validation) */}
          {submitError && (
            <div
              className={`mt-2 p-3 rounded border ${
                isDarkMode
                  ? "bg-red-900/30 border-red-700 text-red-200"
                  : "bg-red-50 border-red-200 text-red-700"
              }`}
            >
              {submitError}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              className="flex-1"
              disabled={isEventCalendarLoading}
            >
              Cancel
            </Button>

            {isEditMode && onDelete && currentBooking && (
              <Button
                type="button"
                variant="destructive"
                onClick={() => handleDelete()}
                className="flex-1"
                disabled={
                  isEventCalendarLoading ||
                  (currentBooking as any).eventStatus === 1
                }
              >
                Delete
              </Button>
            )}

            {hasPatientId && onStartConsultation && (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  if (!hasConsultationPermission) {
                    setShowPermissionMessage(
                      "You don't have permission to start this consultation",
                    );
                    setShowPermissionModal(true);
                    return;
                  }

                  onStartConsultation(booking);
                }}
                className="flex-1 text-white bg-green-600 border-green-600 hover:bg-green-700"
                disabled={isStartConsultationDisabled}
              >
                Start Consultation
              </Button>
            )}

            <Button
              type="submit"
              className="flex-1 text-white bg-slate-900 hover:bg-slate-800"
              disabled={
                isEventCalendarLoading ||
                (isEditMode && (currentBooking as any)?.eventStatus === 1) ||
                (isEditMode && (currentBooking as any)?.eventStatus === 4) ||
                isConsultationCompleted
              }
            >
              {isEventCalendarLoading ? (
                <span className="inline-flex items-center gap-1">
                  {isEditMode ? "Updating" : "Creating"}
                  <span className="">...</span>
                </span>
              ) : isEditMode ? (
                "Update Appointment"
              ) : (
                "Create Appointment"
              )}
            </Button>
          </div>
        </form>
        {/* Delete Confirmation Dialog */}
        {showDeleteConfirm && isEditMode && onDelete && currentBooking && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div
              className={`p-6 rounded-lg max-w-md w-full mx-4 ${
                isDarkMode ? "bg-gray-800 text-white" : "bg-white text-gray-900"
              }`}
            >
              <h3 className="mb-4 text-lg font-semibold">Delete Appointment</h3>
              <p
                className={`${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                } mb-6`}
              >
                Are you sure you want to delete this appointment? This action
                cannot be undone.
              </p>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1"
                  disabled={isEventCalendarLoading}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => {
                    onDelete(currentBooking.id);
                    setShowDeleteConfirm(false);
                    setOpen(false);
                  }}
                  className="flex-1"
                  disabled={isEventCalendarLoading}
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>

      <PatientMatchDialog
        open={showMatchDialog}
        onOpenChange={setShowMatchDialog}
        onMatch={handlePatientMatch}
        isDarkMode={isDarkMode}
        // Use the NHS patient data
        matchPatient={{
          name:
            NHSEmergencyPatientData?.[0]?.patientName ||
            watch("patientName") ||
            "New Patient",
          consultationId: NHSEmergencyPatientData?.[0]?.consultationId || "",
        }}
        NHSEmergencyPatientData={(() => {
          // The newly added patient (first in the list)
          const sourcePatient = localMatchingPatients[0];
          // Other patients to compare against (excluding the source)
          const patientsToCompare =
            localMatchingPatients.length > 1
              ? localMatchingPatients.slice(1)
              : [];

          return patientsToCompare.map((patient, index) => ({
            _id: patient.patientID || patient._id || `temp-${index}`,
            patientID: patient.patientID || patient._id,
            patientName:
              patient.patientName ||
              `${patient.foreName || ""} ${patient.surName || ""}`.trim(),
            nhsNumber: patient.nhsNumber || "Not available",
            dob: patient.dob || new Date().toISOString(),
            address: patient.address || "Address not available",
            // Compare against the source patient
            matchScore: calculateMatchScore(patient, sourcePatient),
            matchReasons: generateMatchReasons(patient, sourcePatient),
            consultationId: patient.consultationId || "",
          }));
        })()}
        isNHSEmergencyPatientLoading={isNHSEmergencyPatientLoading}
        handleAddNewPatient={handleAddNewPatient}
      />
    </Dialog>
  );
}
